import { useEffect, useState } from 'react';
import { apiCall, default as API_CONFIG } from '../config/api';

/**
 * Module-level cache so navigating between /analysis and /analysis/:id
 * doesn't refetch the list. Tiny optimization -- the backend query is
 * already fast -- but it also keeps the detail page rendering instantly
 * when the user clicks a row from the list.
 *
 * Cache is invalidated by reload only -- there's no dependency-tracked
 * staleness here. That's fine for read-only daily reports.
 */
let _cache = null;
let _cacheCount = null;

/**
 * Fetches the recent daily reports list from `/api/reports?count=N`.
 * Returns { reports, loading, error, reload }.
 */
export const useReports = (count = 30) => {
  const [reports, setReports] = useState(_cache && _cacheCount === count ? _cache : []);
  const [loading, setLoading] = useState(!(_cache && _cacheCount === count));
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiCall(API_CONFIG.endpoints.reportsWithCount(count));
      const list = Array.isArray(data) ? data : [];
      _cache = list;
      _cacheCount = count;
      setReports(list);
    } catch (err) {
      console.error('Failed to load reports', err);
      setError('Failed to load analysis reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (_cache && _cacheCount === count) return; // already warm
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  return { reports, loading, error, reload: load };
};
